import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { UserEnvironmentService } from '@examdojo/core/environment';
import { distinctUntilChanged, first, map, of, switchMap } from 'rxjs';
import { UserQuery } from '@examdojo/user';
import { UserSettingsQuery } from './user-settings';

@Injectable()
export class ExamdojoUserEnvironmentService extends UserEnvironmentService {
  constructor(
    private readonly userQuery: UserQuery,
    private readonly userSettingsQuery: UserSettingsQuery,
    @Inject(LOCALE_ID) private readonly localeId: string,
  ) {
    super();
  }

  readonly timezone$ = this.userSettingsQuery.timezone$.pipe(map(() => this.timezone));
  readonly locale$ = of(this.locale);

  private readonly isAppThemeLoaded$ = this.userQuery.isActiveUserLoaded$.pipe(
    switchMap((isActiveUserLoaded) => {
      if (!isActiveUserLoaded) {
        return of(false);
      }

      return this.userQuery.active$.pipe(
        switchMap((user) => {
          if (!user) {
            return of(true);
          }
          return this.userSettingsQuery.appTheme$.pipe(map((appTheme) => appTheme !== undefined));
        }),
      );
    }),
    distinctUntilChanged(),
  );

  readonly appTheme$ = this.isAppThemeLoaded$.pipe(
    first(Boolean),
    switchMap(() => this.userSettingsQuery.appTheme$),
    map((appTheme) => appTheme ?? null),
  );

  get locale(): string | null {
    return this.localeId;
  }

  get appTheme() {
    return this.userSettingsQuery.getValue()?.app_theme ?? null;
  }

  get timezone() {
    const userTimeZone = this.userSettingsQuery.getValue()?.timezone ?? null;
    return this.isValidTimeZone(userTimeZone) ? userTimeZone : null;
  }

  private isValidTimeZone(tz: unknown): boolean {
    try {
      if (typeof tz !== 'string') {
        return false;
      }

      // throws an error if timezone is not valid
      Intl.DateTimeFormat(undefined, { timeZone: tz });
      return true;
    } catch (error) {
      return false;
    }
  }
}
